import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Download, Search, FileText, Calendar, Eye, Pin, ChevronLeft, ChevronRight } from 'lucide-react';
import { supabase } from '../supabase/client';
import { formatDate } from '../utils/timeUtils';

interface DownloadCategory {
  id: number;
  name: string;
  sort_order: number;
}

interface DownloadFile {
  id: number;
  title: string;
  summary: string;
  category_id: number;
  category_name?: string;
  file_url: string;
  file_name: string;
  file_size: number;
  file_type: string;
  download_count: number;
  is_pinned: boolean;
  created_at: string;
}

const formatFileSize = (bytes: number) => {
  if (bytes === 0) return '-';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const getFileIcon = (fileType: string) => {
  if (fileType?.includes('pdf')) return 'PDF';
  if (fileType?.includes('zip') || fileType?.includes('rar')) return 'ZIP';
  if (fileType?.includes('image')) return 'IMG';
  if (fileType?.includes('word') || fileType?.includes('document')) return 'DOC';
  if (fileType?.includes('excel') || fileType?.includes('sheet')) return 'XLS';
  return 'FILE';
};

export default function DownloadCenter() {
  const [categories, setCategories] = useState<DownloadCategory[]>([]);
  const [files, setFiles] = useState<DownloadFile[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const pageSize = 10;

  useEffect(() => {
    fetchCategories();
    fetchFiles();
  }, [selectedCategory, page, searchKeyword]);

  const fetchCategories = async () => {
    const { data } = await supabase
      .from('download_categories')
      .select('*')
      .eq('is_visible', true)
      .order('sort_order');
    if (data) setCategories(data as DownloadCategory[]);
  };

  const fetchFiles = async () => {
    setLoading(true);
    let query = supabase
      .from('download_files')
      .select('*, category_name:download_categories(name)', { count: 'exact' })
      .eq('is_visible', true)
      .order('is_pinned', { ascending: false })
      .order('sort_order')
      .order('created_at', { ascending: false })
      .range((page - 1) * pageSize, page * pageSize - 1);

    if (selectedCategory) {
      query = query.eq('category_id', selectedCategory);
    }
    if (searchKeyword) {
      query = query.ilike('title', `%${searchKeyword}%`);
    }

    const { data, count } = await query;
    if (data) {
      const filesWithCategory = data.map((f: any) => ({
        ...f,
        category_name: f.category_name?.name || '未分类'
      }));
      setFiles(filesWithCategory);
    }
    if (count) setTotalPages(Math.ceil(count / pageSize));
    setLoading(false);
  };

  const handleDownload = async (file: DownloadFile) => {
    await supabase
      .from('download_files')
      .update({ download_count: file.download_count + 1 })
      .eq('id', file.id);
    window.open(file.file_url, '_blank');
    fetchFiles();
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-center gap-2 text-gray-600 hover:text-blue-600">
              <ChevronLeft className="w-5 h-5" /> 返回
            </Link>
            <h1 className="text-xl font-bold text-gray-900">下载中心</h1>
          </div>
        </div>
      </header>

      <section className="bg-gradient-to-br from-blue-500 via-blue-600 to-indigo-700 text-white py-12">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-bold mb-2">资源下载</h2>
          <p className="text-blue-100">精选学习资料，助力编程成长</p>
        </div>
      </section>

      <section className="py-8">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex flex-wrap gap-2 mb-6">
            <button
              onClick={() => { setSelectedCategory(null); setPage(1); }}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                selectedCategory === null ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 border border-gray-200'
              }`}
            >
              全部
            </button>
            {categories.map(cat => (
              <button
                key={cat.id}
                onClick={() => { setSelectedCategory(cat.id); setPage(1); }}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  selectedCategory === cat.id ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 border border-gray-200'
                }`}
              >
                {cat.name}
              </button>
            ))}
          </div>

          <div className="mb-6">
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={searchKeyword}
                onChange={(e) => { setSearchKeyword(e.target.value); setPage(1); }}
                placeholder="搜索资料..."
                className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none"
              />
            </div>
          </div>

          <div className="space-y-4">
            {loading ? (
              <div className="text-center py-12 text-gray-500">加载中...</div>
            ) : files.length === 0 ? (
              <div className="text-center py-12 text-gray-500">暂无资料</div>
            ) : (
              files.map((file, index) => (
                <motion.div
                  key={file.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className={`bg-white rounded-xl border p-6 hover:shadow-lg transition-shadow ${
                    file.is_pinned ? 'border-blue-300 bg-blue-50' : 'border-gray-100'
                  }`}
                >
                  <div className="flex items-start gap-4">
                    <div className="w-14 h-14 rounded-xl bg-blue-100 text-blue-600 flex items-center justify-center flex-shrink-0 font-bold text-xs">
                      {getFileIcon(file.file_type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs">{file.category_name}</span>
                        {file.is_pinned && (
                          <span className="flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs">
                            <Pin className="w-3 h-3" /> 置顶
                          </span>
                        )}
                      </div>
                      <h3 className="text-lg font-bold text-gray-900 mb-1">{file.title}</h3>
                      <p className="text-gray-500 text-sm mb-3 line-clamp-2">{file.summary || '暂无简介'}</p>
                      <div className="flex items-center gap-4 text-sm text-gray-400">
                        <span className="flex items-center gap-1"><Calendar className="w-4 h-4" />{formatDate(file.created_at)}</span>
                        <span className="flex items-center gap-1"><FileText className="w-4 h-4" />{formatFileSize(file.file_size)}</span>
                        <span className="flex items-center gap-1"><Eye className="w-4 h-4" />{file.download_count}次下载</span>
                      </div>
                    </div>
                    <button
                      onClick={() => handleDownload(file)}
                      className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                    >
                      <Download className="w-4 h-4" /> 下载
                    </button>
                  </div>
                </motion.div>
              ))
            )}
          </div>

          {totalPages > 1 && (
            <div className="flex justify-center gap-2 mt-8">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-4 py-2 rounded-lg border border-gray-200 disabled:opacity-50"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <span className="px-4 py-2 text-gray-600">{page} / {totalPages}</span>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="px-4 py-2 rounded-lg border border-gray-200 disabled:opacity-50"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
